import { Link } from "@tanstack/react-router";
import { FileText, X } from "lucide-react";
import { useCart } from "@/lib/cart";
import { formatPrice } from "@/lib/store.queries";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

/**
 * Cart drawer for the research store. Checkout hands off to Shopify's hosted
 * checkout — quantities are fixed at one per report, so lines are add/remove only.
 */
export function CartSheet() {
  const { cart, open, setOpen, removeItem, removing } = useCart();
  const lines = cart?.lines ?? [];
  const currency = cart?.currency ?? "USD";

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" aria-describedby={undefined} className="flex w-full flex-col p-0 sm:max-w-md">
        <SheetHeader className="border-b p-6 pb-4">
          <SheetTitle className="text-left text-base">
            Your cart{lines.length > 0 ? ` (${lines.length})` : ""}
          </SheetTitle>
        </SheetHeader>

        {lines.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
            <FileText className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Your cart is empty.</p>
            <Button asChild variant="outline" size="sm">
              <Link to="/research" onClick={() => setOpen(false)}>
                Browse research
              </Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <ul className="space-y-4">
                {lines.map((line) => (
                  <li key={line.lineId} className="flex items-start gap-3">
                    {line.imageUrl ? (
                      <img
                        src={line.imageUrl}
                        alt=""
                        className="h-16 w-12 shrink-0 rounded border object-cover"
                      />
                    ) : (
                      <div className="flex h-16 w-12 shrink-0 items-center justify-center rounded border bg-muted text-muted-foreground">
                        <FileText className="h-4 w-4" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium leading-snug">{line.title}</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {formatPrice(line.priceCents, line.currency)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0"
                      aria-label={`Remove ${line.title}`}
                      disabled={removing}
                      onClick={() => removeItem(line.lineId)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-t p-6">
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-medium">Subtotal</span>
                <span className="text-lg font-semibold">
                  {formatPrice(cart?.subtotalCents ?? 0, currency)}
                </span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Taxes calculated at checkout.</p>
              <Button
                className="mt-4 w-full"
                onClick={() => {
                  if (cart?.checkoutUrl) window.location.assign(cart.checkoutUrl);
                }}
              >
                Checkout
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
